<template>
  <div>
    <AlertBanner v-for="(alert, i) in alertStore.getVisibleAlerts" :key="i" :info="alert" />
    <table>
      <thead>
      <tr>
        <th v-for="(item, i) in headers" :key="i" :class="getColumnClass(item.field)">
          <template v-if="item.visible">
            {{ item.name }}
          </template>
        </th>
      </tr>
      </thead>
      <tbody v-if="servicesAndAgreements.length">
      <tr v-for="item in servicesAndAgreements">
        <td class="icon-column">
          <svg v-if="item.type === ServiceAndAgreementType.Agreement" aria-hidden="true" aria-label="pen-to-square-icon"
               viewBox="0 0 512 512">
            <g class="svg__color" fill="currentColor" fill-rule="evenodd">
              <penToSquareIcon />
            </g>
          </svg>
          <i v-if="item.type === 'Service'" class="fas fa-book-open"></i>
        </td>
        <td>
          <template v-if="item.type === ServiceAndAgreementType.Agreement">Avtal</template>
          <template v-if="item.type === ServiceAndAgreementType.Service">Tjänst</template>
        </td>
        <td>
            <router-link v-if="item.type === ServiceAndAgreementType.Agreement && !(item.isOld && item?.status == 'Signed')"
                         :to="`${item.link}${item.isOld ? 'old' : 'current'}`">
                Överenskommelse &
                PUB-avtal {{ item.templateVersion }}
            </router-link>
            <span v-else-if="item.type === ServiceAndAgreementType.Agreement">
                Överenskommelse &
                PUB-avtal {{ item.templateVersion }}
            </span>
          <a v-if="item.type === ServiceAndAgreementType.Service" :href="item.link">
              {{ item.name }} {{ item.templateVersion }}
          </a>
          &nbsp;<i v-if="item.type === ServiceAndAgreementType.Service && item.link"
                   class="fa fa-external-link-alt fa-xs"></i>
        </td>
        <td class="status-column">
          <template v-if="item.type === ServiceAndAgreementType.Agreement">
            <span v-if="item?.status === 'Inactive'"><Symbol
                    :accessibilityId="'Hjälp ' + item.id"
                    :symbolMessage="getSymbolText(item?.status)"
                    type="warning-agreement"/>
            {{ getAgreementStatusText(item?.status) }}</span>
            <span v-else>{{ getAgreementStatusText(item?.status) }}</span>
            </template>
          <template v-if="item.type === ServiceAndAgreementType.Service && dateCompare(item) !== DateStatus.NoLicense">
            <span>Giltig t.o.m </span>
            <span
              :class="{'red-color': (dateCompare(item) === DateStatus.Expired || dateCompare(item) === DateStatus.Expiring)}"
              class="status-container">{{ getServiceStatusText(item)}}</span>
          </template>
        </td>
        <td class="action-column">
            <template v-if="item.type === ServiceAndAgreementType.Agreement">
                <router-link v-if="item?.status === 'Inactive'" :to="`${orgUrl}/avtal/intro`">
                    <span>{{ getAgreementActionText(item?.status) }}</span>
                </router-link>
            </template>
            <template v-if="item.type === ServiceAndAgreementType.Service && !shouldDisableLicenseLink()">
                <a v-if="item.statusLink"
                :href="item.statusLink"
                class="ml-1">{{ item.statusLinkText }}</a>&nbsp;<i
              v-if="item.statusLink"
              class="fa fa-external-link-alt fa-xs"></i>
            </template>
            <template v-else>
              <span v-if="item.statusLink"
              class="ml-1 disabled-link">
              {{ item.statusLinkText }}
              </span>&nbsp;
              <i
              v-if="item.statusLink"
              class="fa fa-external-link-alt fa-xs disabled-link"></i>
            </template>
        </td>
      </tr>
      </tbody>
    </table>
    <p v-if="!servicesAndAgreements.length">
      Inga avtal hittades
    </p>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "@vue/runtime-core";
import Symbol from "../components/Symbol.vue";
import { TableHeader } from "@/static/types/TableHeader";
import { useRoute } from "vue-router";
import { useOrganisationStore } from "@/stores/organisation";
import { storeToRefs } from "pinia";
import { getColumnClass } from "@/helper/getColumnClass";
import penToSquareIcon from "./svg/pen-to-square-icon.vue";
import { ServicesAndAgreementsListModel } from "@Shared/Models/ServicesAndAgreementsListModel";
import AlertBanner from "./AlertBanner.vue";
import { useAlertStore } from "@/stores/alert";
import { watch } from "vue";

enum ServiceAndAgreementType {
  Service = "Service",
  Agreement = "Agreement"
}

enum DateStatus {
  Expired = "Expired",
  Expiring = "Expiring",
  Active = "Active",
  NoLicense = "NoLicense"
}

export default defineComponent({
  components: {
    penToSquareIcon,
    Symbol,
    AlertBanner
  },
  name: "TableAgreement",
  setup() {
    const route = useRoute();
    const orgUrl = `/organisation/${route.params.orgId}`;
    const organisationStore = useOrganisationStore();
    const { servicesAndAgreements } = storeToRefs(organisationStore);
    const alertStore = useAlertStore();

    watch(servicesAndAgreements, (val, oldVal) => {

      if (!val?.length) {
        return;
      }

      if (JSON.stringify(val) === JSON.stringify(oldVal)) {
        return;
      }

      const result = shouldDisableLicenseLink();
      const hasTwoAgreements = (servicesAndAgreements.value?.filter(x => x.type === ServiceAndAgreementType.Agreement).length ?? 0) >= 2;

      if (result && hasTwoAgreements) {
        const alert = {
          showAlertBanner: true,
          headerText: "Viktigt!",
            informationText: "PUB-avtalet behöver tecknas om för din organisation"
        };

    alertStore.setAlerts([alert]);

  } else {
    alertStore.clearAlerts(); 
  }
});

    const getStatusText = (
      status:
        | "Inactive"
        | "Signed"
        | "Approved"
        | "Active"
        | "Dormant"
        | "Expired"
        | "Dismissed"
        | ""
    ): string => {
      switch (status) {
        case "Inactive":
          return "Ej undertecknat";
        case "Signed":
          return "Inväntar granskning av SPSM";
        case "Approved":
          return "Godkänd";
        case "Active":
          return "Aktivt";
        case "Dismissed":
          return "Ej godkänd";
        case "Expired":
          return "Utgången";
        case "Dormant":
          return "Vilande";
        default:
          return "";
      }
    };

    const getActionText = (
      status:
        | "Inactive"
        | "Signed"
        | "Approved"
        | "Active"
        | "Dormant"
        | "Expired"
        | "Dismissed"
        | ""
    ): string => { 
      switch (status) {
        case "Inactive":
          return "Underteckna";
        default:
          return "";
      }
    };

    const getSymbolText = (
      status:
        | "Inactive"
        | "Signed"
        | "Approved"
        | "Active"
        | "Dormant"
        | "Expired"
        | "Dismissed"
        | ""
    ): string => {
      switch (status) {
        case "Inactive":
          return "Du behöver underteckna överenskommelse och PUB-avtal";
        case "Signed":
          return "SPSM behandlar inom kort";
        case "Approved":
          return "";
        case "Active":
          return "";
        case "Expired":
          return "";
        case "Dismissed":
          return "Ta kontakt med SPSM";
        case "Dormant":
          return "Ingen aktiv licens";
        default:
          return "";
      }
    };

    const headers: TableHeader[] = [
      {
        field: "icon",
        name: "Icon",
        visible: false
      },
      {
        field: "type",
        name: "Typ",
        visible: true
      },
      {
        field: "name",
        name: "Namn",
        visible: true
      },
      {
        field: "status",
        name: "Status",
        visible: true
      },
      {
        field: "action",
        name: "Åtgärd",
        visible: true
      },
    ];

    const getServiceStatusText = (item: ServicesAndAgreementsListModel): string => {
      if (!item.validUntil && !hasSignedPubAgreement()) {
        return "Ingen licens";
      } else if (!item.validUntil && hasSignedPubAgreement()) {
        return "";
      }

      const dateStatus = dateCompare(item);
      const validUntilDate = new Date(item.validUntil);
      switch (dateStatus) {
        case DateStatus.Expired:
          return validUntilDate.toLocaleDateString("sv-SE", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric"
          });
        case DateStatus.Expiring:
          return validUntilDate.toLocaleDateString("sv-SE", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric"
          });
        case DateStatus.Active:
          return validUntilDate.toLocaleDateString("sv-SE", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric"
          });
        default:
          return "";
      }
    };

    const dateCompare = (item: ServicesAndAgreementsListModel) => {
      if (!item.validUntil) {
        return DateStatus.NoLicense;
      }
      const validUntilDate = new Date(item.validUntil);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayTimestamp = today.getTime();
      const validUntilDateTimestamp = validUntilDate.getTime();


      const fourteenDaysInMillis = 14 * 24 * 60 * 60 * 1000; // 14 days in milliseconds
      const daysDifference = Math.abs(today.getTime() - validUntilDate.getTime()) / fourteenDaysInMillis;
      if (daysDifference <= 1 && validUntilDateTimestamp > todayTimestamp) {
        return DateStatus.Expiring;
      }

      if (validUntilDateTimestamp > todayTimestamp) {
        return DateStatus.Active;
      }

      if (validUntilDateTimestamp <= todayTimestamp) {
        return DateStatus.Expired;
      } else {
        return DateStatus.NoLicense;
      }
    };


    const getOrgSizeText = (orgSize): string => {
      switch (orgSize) {
        case "XSmall":
          return " - extra liten";
        case "Small":
          return " - liten";
        case "Medium":
          return " - mellan";
        case "Large":
          return " - stor";
        default:
          return "";
      }
    };

    const hasSignedPubAgreement = () => {
      return organisationStore.servicesAndAgreements.some(x =>
        x.type === ServiceAndAgreementType.Agreement &&
        (x.status === "Active" || x.status === "Approved")
      );
    };

    const hasUserSignedPubAgreement = () => {
      return organisationStore.servicesAndAgreements.some(x =>
        x.type === ServiceAndAgreementType.Agreement &&
        (!x.isOld &&
              (x.status === "Active" || x.status === "Approved"))
      );
    };

      const IsDormantAndOld = () => {
          const items = organisationStore.servicesAndAgreements
              .filter(x => x.type === ServiceAndAgreementType.Agreement);

          // Endast ett avtal
          if (items.length === 1) {
              const x = items[0];

              if (
                  x.status === "Active" ||
                  x.status === "Approved" ||
                  (x.status === "Dormant" && !x.isOld)
              ) {
                  return false;
              }

              return true;
          }

          // Flera avtal
          return items.some(x =>
              x.isOld &&
              x.status === "Dormant"
          );
      };

      const shouldDisableLicenseLink = () => {
          const hasValidAgreement = hasUserSignedPubAgreement();

          const agreements =
              servicesAndAgreements.value?.filter(
                  x => x.type === ServiceAndAgreementType.Agreement
              ) ?? [];

          const realServices =
              servicesAndAgreements.value?.filter(
                  x =>
                      x.type === ServiceAndAgreementType.Service &&
                      !x.isPlaceholder
              ) ?? [];

          const hasDormantAgreement = agreements.some(
              x => x.status === "Dormant"
          );

          if (realServices.length === 0) {
              return !hasValidAgreement;
          }

          if (hasValidAgreement) {
              return false;
          }

          if (IsDormantAndOld()) {
              return true;
          }

          if (hasDormantAgreement) {
              return !realServices.some(item =>
                  [
                      DateStatus.NoLicense,
                      DateStatus.Expired,
                      DateStatus.Expiring
                  ].includes(dateCompare(item))
              );
          }

          return true;
      };

    return {
      headers,
      servicesAndAgreements,
      orgUrl,
      getColumnClass,
      getAgreementStatusText: getStatusText,
      getAgreementActionText: getActionText,
      getSymbolText,
      ServiceAndAgreementType,
      getServiceStatusText,
      dateCompare,
      DateStatus,
      getOrgSizeText,
      hasSignedPubAgreement,
      alertStore,
      shouldDisableLicenseLink
    };
  }
});

</script>

<style lang="scss">
@import '../assets/table.scss';

.red-color {
  color: var(--spsm-red-1);
}

.disabled-link {
  color: #999;
  cursor: not-allowed;
  pointer-events: none;
  text-decoration: none;
}

.status-container {
  margin: 0;
  padding: 0;
}
</style>